var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import React, { useState } from 'react';
import { Accordion, Button, Checkbox, InlineNotification, Table, Text } from '@ui';
import { dismissPullFailure, startBulkPull } from '../shared/api';
import { FAILURE_REASONS, PULL_STATUS } from '../shared/constants';
import { translatorFor } from '../shared/i18n';
import { errorResponse, humanize } from '../shared/types';
const t = translatorFor('pull_card');
function lastPullLine(summary, running) {
    if (running || (summary === null || summary === void 0 ? void 0 : summary.status) === PULL_STATUS.RUNNING)
        return t('running');
    if (!summary)
        return t('never_pulled');
    return t('last_pull_line', {
        when: new Date(summary.at).toLocaleString(),
        created: summary.buildingsCreated,
        existing: summary.buildingsAlreadyThere,
        failed: summary.failed
    });
}
export function PullCard({ urls, pullSummary, running, onPullSummaryChange, onPullStarted }) {
    var _a;
    const [expanded, setExpanded] = useState(pullSummary === null || running);
    const [includeEquipment, setIncludeEquipment] = useState(true);
    const [includeContacts, setIncludeContacts] = useState(true);
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState(null);
    const [showFailures, setShowFailures] = useState(false);
    const failureRows = ((_a = pullSummary === null || pullSummary === void 0 ? void 0 : pullSummary.failures) !== null && _a !== void 0 ? _a : []).map((failure) => (Object.assign(Object.assign({ id: `${failure.objectType}-${failure.serviceTitanId}` }, failure), { objectType: humanize(failure.objectType) })));
    const columns = [
        { dataKey: 'objectType', label: t('type_heading'), columnType: 'primary', width: '25%' },
        { dataKey: 'serviceTitanId', label: t('id_heading'), width: '25%' },
        { dataKey: 'reason', label: t('reason_heading'), width: '50%' }
    ];
    const pull = () => __awaiter(this, void 0, void 0, function* () {
        var _a;
        setSubmitting(true);
        setError(null);
        try {
            onPullStarted(yield startBulkPull(urls, { includeEquipment, includeContacts }));
        }
        catch (err) {
            const data = errorResponse(err);
            setError((data === null || data === void 0 ? void 0 : data.reason) === FAILURE_REASONS.ALREADY_RUNNING ? t('already_running') : (_a = data === null || data === void 0 ? void 0 : data.error) !== null && _a !== void 0 ? _a : t('start_failed'));
        }
        finally {
            setSubmitting(false);
        }
    });
    const dismiss = () => __awaiter(this, void 0, void 0, function* () {
        try {
            onPullSummaryChange(yield dismissPullFailure(urls));
        }
        catch (_a) {
            setExpanded(true);
            setError(t('dismiss_failed'));
        }
    });
    const disabled = running || submitting;
    return (React.createElement(React.Fragment, null,
        (pullSummary === null || pullSummary === void 0 ? void 0 : pullSummary.needsAttention) && (React.createElement(InlineNotification, { variant: "warning", dismissible: true, onDismiss: () => {
                void dismiss();
            }, action: React.createElement(Button, { variant: "tertiary", onClick: () => {
                    setExpanded(true);
                    setShowFailures(true);
                } }, t('view_failures')) }, t('pull_failed_notice', { count: pullSummary.failed }))),
        React.createElement(Accordion, { variant: "section", title: t('title'), isExpanded: expanded, onToggle: setExpanded },
            React.createElement("div", { className: "service-titan-settings__accordion-body" },
                React.createElement("div", { className: "service-titan-settings__line" },
                    React.createElement(Text, { variant: "body", as: "p", className: "service-titan-settings__muted" }, lastPullLine(pullSummary, running)),
                    pullSummary && !running && (React.createElement(Button, { variant: "tertiary", onClick: () => setShowFailures((previous) => !previous) }, t('view')))),
                showFailures && (React.createElement(Table, { data: failureRows, columns: columns, getItemId: (row) => row.id, emptyContent: React.createElement(Text, { variant: "body" }, t('no_failures')), variant: "listing" })),
                React.createElement("div", { className: "service-titan-settings__actions" },
                    React.createElement(Checkbox, { id: "service-titan-pull-include-contacts", checked: includeContacts, disabled: disabled, onChange: setIncludeContacts, label: t('include_contacts_label') }),
                    React.createElement(Checkbox, { id: "service-titan-pull-include-equipment", checked: includeEquipment, disabled: disabled, onChange: setIncludeEquipment, label: t('include_equipment_label') }),
                    React.createElement(Button, { variant: "primary", disabled: disabled, onClick: () => {
                            void pull();
                        } }, submitting ? t('starting') : t('pull_buildings'))),
                error && React.createElement(InlineNotification, { variant: "error" }, error)))));
}
export default PullCard;
